// ★ PHOTOGRAPHY SETTINGS — what every AI-generated image is actually asked for.
//
// ── Omar, 2026-09-08 (verbatim) ─────────────────────────────────────────────
//
//   "Please make sure to apply the same style to the 'Image profiles' section in
//    the Console. I don't want a full long page but tabs to go through each i.e.
//    VISUAL CRAFT, CONTEXT AND TONE, Illustration style, What can never be
//    changed, and why, Exactly what gets sent. Also the section should be called
//    Photography settings or something similar."
//
// His five tab names are the five section headings he read off the old page, so
// they are used exactly as he wrote them. The two in capitals are not shouting:
// `VISUAL CRAFT` and `CONTEXT AND TONE` are the literal section headers sent to
// the model (`SECTION_CRAFT` / `SECTION_GUIDANCE`), which is why he saw them that
// way. The TAB labels use sentence case, matching the company page's strip; the
// headings inside each panel keep the model's own capitals, because that is what
// is actually sent.
//
// ── ⚠️ THE NAME ─────────────────────────────────────────────────────────────
//
// "Photography settings" is his suggestion and is used. It is very slightly
// narrower than the contents — the Illustration style tab is here too — so the
// caveat is worth stating rather than quietly resolving: the two EDITABLE things
// on this screen are both about photography, and the illustration tab is
// reference-only, pointing at the company page where that setting actually
// lives. "or something similar" was his own latitude; if the mismatch bothers
// him, "Image settings" is the alternative and it is one string.
//
// ── What did NOT change, and must not ───────────────────────────────────────
//
// The screen edits the two SHARED layers and no more. The illustration style is
// deliberately NOT editable here — it belongs to one customer and lives on that
// customer's page. An editable copy here as well would be two homes for one
// setting (`feedback_one_rule_one_place`).
//
// ★ AND ONE THING GOT BETTER IN THE REORGANISATION, rather than merely moving.
//   The old page rendered the mandatory output rules for whichever KIND was
//   selected in a control further down, so the "what can never be changed"
//   section silently said something different depending on a widget that was
//   often off-screen. Split across tabs that would have become a value shown on
//   one tab and controlled from another — invisible, and wrong. Both variants are
//   now shown side by side and the tab needs no control at all.
//
// ⚠️ Each section still states whether it can ever be changed and by whom. A page
// showing profile text with no way to change it, and no explanation, reads as a
// broken editor (`feedback_a_greyed_control_is_not_a_missing_capability`).
//
// Why the Console can import @dynamo/schema when apps/fe cannot: this app is
// built by Vite. The authoring frontend is classic scripts parsed in the browser
// and needs a generated copy; here the real module is the one home.

import { useMemo, useState } from "react";

import type { ConsoleApi, SharedImageProfile } from "./api.js";
import { ProfileLayerEditor } from "./profile-editor.js";
import { TabPanel, Tabs } from "./tabs.js";
import { CODE_STYLE, ErrorState, Loading, fmtDateTime, useAsync } from "./ui.js";

/** Must match `PROFILE_TEXT_MAX` in the gateway's admin routes. */
const PROFILE_TEXT_MAX = 20_000;

import {
  FACTORY_IMAGE_PROFILE,
  HARD_SECTIONS,
  type ImageProfile,
  type ImageVisualStyle,
  MEDIA_SLOTS,
  type MediaSlotSpec,
  SECTION_CRAFT,
  SECTION_GUIDANCE,
  SECTION_SAFETY,
  SECTION_SLOT,
  composeImagePrompt,
  describeSlotSize,
  hardSafetyRules,
} from "@dynamo/schema";

/**
 * ★ HIS FIVE, IN HIS ORDER. The labels are his own words; only the capitals of
 * the first two are softened, because a tab strip in block capitals reads as
 * shouting while the same words as a heading inside the panel are quoting the
 * instruction actually sent.
 */
const PROFILE_TABS = [
  { id: "craft", label: "Visual craft" },
  { id: "tone", label: "Context and tone" },
  { id: "illustration", label: "Illustration style" },
  { id: "fixed", label: "What can never be changed, and why" },
  { id: "sent", label: "Exactly what gets sent" },
] as const;

type ProfileTabId = (typeof PROFILE_TABS)[number]["id"];

function Note({ children }: { children: React.ReactNode }) {
  return (
    <p style={{ margin: "0 0 10px", fontSize: 12.5, lineHeight: 1.6, color: "var(--text-muted)" }}>
      {children}
    </p>
  );
}

/** A block of profile text, labelled with who — if anyone — can ever change it. */
function Layer({
  title,
  editable,
  reason,
  body,
}: {
  title: string;
  editable: boolean;
  reason: string;
  body: string;
}) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <h3 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "var(--text-strong)" }}>
          {title}
        </h3>
        <span className={editable ? "pill pending" : "pill"}>
          {editable ? "Editable — later" : "Fixed"}
        </span>
      </div>
      <Note>{reason}</Note>
      <pre style={CODE_STYLE}>{body}</pre>
    </div>
  );
}

export function ImageProfilesScreen({ api }: { api: ConsoleApi }) {
  const { state, retry } = useAsync<SharedImageProfile>(() => api.imageProfile(), []);
  const [tab, setTab] = useState<ProfileTabId>("craft");
  const generatable = MEDIA_SLOTS;
  const [slotKey, setSlotKey] = useState(
    `${generatable[0]?.layoutType ?? ""}.${generatable[0]?.field ?? ""}`,
  );
  const [style, setStyle] = useState<ImageVisualStyle>("photograph");

  const slot: MediaSlotSpec | undefined = useMemo(
    () => generatable.find((s) => `${s.layoutType}.${s.field}` === slotKey),
    [generatable, slotKey],
  );

  // ★ THE PREVIEW USES WHAT IS ACTUALLY STORED, not the factory constant.
  //   A preview that showed the factory text while the model received edited
  //   text would be worse than no preview at all: it would look like evidence.
  //   `illustration` stays the factory text here because this screen has no
  //   customer selected — the note under the preview says so rather than
  //   implying every customer gets these words.
  const profile: ImageProfile = useMemo(
    () =>
      state.t === "ok"
        ? {
            ...FACTORY_IMAGE_PROFILE,
            craft: state.data.craft,
            guidance: state.data.guidance,
          }
        : FACTORY_IMAGE_PROFILE,
    [state],
  );

  // The REAL composition, not a description of it. Whatever this screen shows is
  // what a generation with these choices would send
  // (`feedback_verify_the_control_not_just_the_pipeline`).
  const composed = useMemo(() => {
    if (!slot) return null;
    try {
      return composeImagePrompt({
        slot,
        style,
        profile,
        authorPrompt: "<the author's own description goes here>",
        // ★ NO PALETTE, deliberately. This screen has no workspace selected, so
        // it does not know any workspace's colours — and an invented sample set
        // would be a plausible placeholder, which is worse than an obviously
        // absent one (`feedback_a_plausible_placeholder_is_worse_than_an_obvious_one`).
        // It also keeps the console free of colour literals, which its own
        // design-system guard bans outright and was right to flag.
      });
    } catch {
      return null;
    }
  }, [slot, style, profile]);

  return (
    <div className="console-page">
      <h1 style={{ margin: "0 0 6px", fontSize: 17, fontWeight: 600, color: "var(--text-strong)" }}>
        Photography settings
      </h1>
      <Note>
        The instructions every AI-generated image is made from. This screen shows exactly what is
        sent to the model, and which parts of it can ever be changed.
      </Note>

      {/* ⚠️ "The profile in use" — name, identifier and text checksum — was REMOVED
          on 2026-09-15 at Omar's explicit instruction ("remove the whole module").
          It is recorded here rather than silently deleted because the block was
          deliberately KEPT through the 2026-09-08 reorganisation on the grounds
          that a change should not quietly drop a panel
          (`feedback_do_not_remove_a_control_the_change_made_redundant`) — so the
          removal needed to be asked for, and it was.

          Nothing depended on it: the identity that MATTERS is the provenance
          fingerprint the gateway records beside each generated image, which is a
          SHA-256 over `profileFingerprintSource` and was never this number. The
          display checksum shown here was a separate, weaker hash that existed
          only for comparing two environments by eye. */}
      <Tabs tabs={PROFILE_TABS} current={tab} onSelect={setTab} idPrefix="profile" />

      {state.t === "loading" && (
        <TabPanel id={tab} idPrefix="profile">
          <Loading label="the shared instructions" />
        </TabPanel>
      )}
      {state.t === "err" && (
        <TabPanel id={tab} idPrefix="profile">
          <ErrorState err={state.err} onRetry={retry} />
        </TabPanel>
      )}

      {state.t === "ok" && tab === "craft" && (
        <TabPanel id="craft" idPrefix="profile">
          <Note>
            Shared by <strong>every customer</strong>. Saving changes what future images are made
            from; pictures already generated are files and are not touched.
          </Note>
          <ProfileLayerEditor
            title={`${SECTION_CRAFT} — how the picture is made`}
            description="Photographic craft: shot size, composition, lens, depth of field, light, colour, realism. This is the part a photographer would argue about."
            value={state.data.craft}
            source={state.data.sources.craft}
            factory={state.data.factory.craft}
            maxLength={PROFILE_TEXT_MAX}
            onSave={async (next) => {
              const r = await api.saveImageProfile({ craft: next });
              return { value: r.craft, source: r.sources.craft };
            }}
            onSaved={retry}
          />
          <Note>
            Last changed: {fmtDateTime(state.data.updatedAt)}
            {state.data.updatedBySub ? " · by a member of Dynamo staff" : ""}.
          </Note>
        </TabPanel>
      )}

      {state.t === "ok" && tab === "tone" && (
        <TabPanel id="tone" idPrefix="profile">
          <Note>
            Shared by <strong>every customer</strong>, and applied after the craft instructions
            above.
          </Note>
          <ProfileLayerEditor
            title={`${SECTION_GUIDANCE} — what it should feel like`}
            description="Tone and restraint for corporate learning: credibility over polish, room for the words that sit on top, who appears and how."
            value={state.data.guidance}
            source={state.data.sources.guidance}
            factory={state.data.factory.guidance}
            maxLength={PROFILE_TEXT_MAX}
            onSave={async (next) => {
              const r = await api.saveImageProfile({ guidance: next });
              return { value: r.guidance, source: r.sources.guidance };
            }}
            onSaved={retry}
          />
          <Note>
            Last changed: {fmtDateTime(state.data.updatedAt)}
            {state.data.updatedBySub ? " · by a member of Dynamo staff" : ""}.
          </Note>
        </TabPanel>
      )}

      {tab === "illustration" && (
        <TabPanel id="illustration" idPrefix="profile">
          <Note>
            Illustrations do <strong>not</strong> use the shared photography instructions. Each
            customer has its own illustration style, edited on that company&rsquo;s own page, so
            changing one customer&rsquo;s style never affects another. Open{" "}
            <strong>Customers → the company → Illustration</strong>.
          </Note>
          <Note>
            A customer that has never had one set uses the protected original below. It is shown here
            for reference and is not editable on this screen.
          </Note>
          <Layer
            title="The original illustration style"
            editable={false}
            reason="Used by any customer whose own style has not been set. An illustration is the only kind of picture the workspace brand colours ever reach; a photograph never gets them."
            body={FACTORY_IMAGE_PROFILE.illustration}
          />
        </TabPanel>
      )}

      {tab === "fixed" && (
        <TabPanel id="fixed" idPrefix="profile">
          <Note>
            These two are added <strong>last</strong>, after everything above, which is what makes
            them impossible to override from the editable text. A profile edited to say
            &ldquo;make it square and add a caption&rdquo; still produces an exact-size, text-free
            image.
          </Note>

          {/* ★ BOTH VARIANTS, SIDE BY SIDE, AND NO CONTROL ON THIS TAB.
              The old page rendered whichever kind was selected in a widget much
              further down. Behind a tab that becomes a value shown on one tab and
              changed from another — which is invisible, and wrong. Showing both is
              also simply more useful: the difference between them is one line. */}
          <Layer
            title={`${SECTION_SAFETY} — the output rules`}
            editable={false}
            reason="No text, logos or watermarks in the picture; no identifiable real people or public figures. Invented employees, customers and staff are the normal, expected result — the restriction is about recognisable real individuals. The first three rules apply to every picture; the fourth is the only one that differs by kind, and both versions are shown."
            body={[
              ...hardSafetyRules("photograph").slice(0, -1),
              "",
              `For a photograph — ${hardSafetyRules("photograph").slice(-1)[0] ?? ""}`,
              `For an illustration — ${hardSafetyRules("illustration").slice(-1)[0] ?? ""}`,
            ].join("\n")}
          />

          <div style={{ marginBottom: 4 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <h3 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "var(--text-strong)" }}>
                {SECTION_SLOT} — the size each place needs
              </h3>
              <span className="pill">Fixed</span>
            </div>
            <Note>
              The size is decided by where the picture goes, never by what the author types. A picture
              of the wrong shape is not cropped by the player — it makes the screen taller or shorter,
              which is how eight images in a shipped course ended up visibly wrong.
            </Note>
            <table className="table">
              <thead>
                <tr>
                  <th>Where it goes</th>
                  <th>What it is called there</th>
                  <th>Size it must come back as</th>
                </tr>
              </thead>
              <tbody>
                {generatable.map((s) => (
                  <tr key={`${s.layoutType}.${s.field}`}>
                    <td data-label="Where it goes" style={{ fontFamily: "var(--font-mono)", fontSize: 11.5 }}>
                      {s.layoutType}
                    </td>
                    <td data-label="What it is called there">{s.label}</td>
                    <td data-label="Size it must come back as">{describeSlotSize(s)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabPanel>
      )}

      {tab === "sent" && (
        <TabPanel id="sent" idPrefix="profile">
          <Note>
            Composed by the same code the generator uses, so this is the real instruction and not a
            description of it. The author&rsquo;s own words replace the placeholder in the first
            section.
          </Note>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: "var(--text-muted)" }}>
              Place{" "}
              <select
                value={slotKey}
                onChange={(e) => setSlotKey(e.target.value)}
                style={{ fontSize: 12, padding: "4px 6px" }}
              >
                {generatable.map((s) => (
                  <option key={`${s.layoutType}.${s.field}`} value={`${s.layoutType}.${s.field}`}>
                    {s.layoutType} · {s.label}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ fontSize: 12, color: "var(--text-muted)" }}>
              Kind{" "}
              <select
                value={style}
                onChange={(e) => setStyle(e.target.value as ImageVisualStyle)}
                style={{ fontSize: 12, padding: "4px 6px" }}
              >
                <option value="photograph">Photograph</option>
                <option value="illustration">Illustration</option>
              </select>
            </label>
          </div>
          {composed ? (
            <>
              <Note>
                Sections in order:{" "}
                {composed.sections.map((s, i) => (
                  <span key={s}>
                    {i > 0 ? " → " : ""}
                    <strong>{s}</strong>
                    {HARD_SECTIONS.includes(s) ? " (fixed)" : ""}
                  </span>
                ))}
                . Brand colours {composed.brandPaletteApplied ? "are" : "are not"} applied here, and
                the file comes back as {composed.outputMimeType === "image/png" ? "PNG" : "JPG"}.
              </Note>
              <Note>
                For an <strong>illustration</strong>, the workspace&rsquo;s own brand colours are
                inserted into the section above — that is the only kind of picture they ever reach. No
                workspace is selected here, so no colours appear in this preview; a photograph never
                gets them in any case.
              </Note>
              <pre style={CODE_STYLE}>{composed.prompt}</pre>
            </>
          ) : (
            <Note>No place selected.</Note>
          )}
        </TabPanel>
      )}
    </div>
  );
}
